import escapeHTML from './escapeHTML';
import {LANGUAGE_MARK_ATTRIBUTES, isSafeLanguageMark} from './languageMarks';
import parseHTMLBody, {
  type ParsedElement,
  type ParsedNode,
} from './parseHTMLBody';

const DEFAULT_ALLOWED_TAGS: Array<string> = [
  'A',
  'B',
  'BLOCKQUOTE',
  'BR',
  'CAPTION',
  'CODE',
  'DD',
  'DIV',
  'DL',
  'DT',
  'EM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HR',
  'I',
  'IMG',
  'LI',
  'NL',
  'OL',
  'P',
  'PRE',
  'SPAN',
  'STRIKE',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TH',
  'THEAD',
  'TR',
  'UL',
];

const DEFAULT_ALLOWED_ATTRIBUTES: Record<string, ReadonlyArray<string>> = {
  // `*` lists the attributes that every allowed tag keeps.
  '*': LANGUAGE_MARK_ATTRIBUTES,
  A: ['href', 'title', 'target'],
  IMG: ['alt', 'src', 'title'],
};

const ALLOWED_SCHEMES: Array<string> = ['http://', 'https://', 'mailto:'];

// Characters a browser discards from the ends of a URL before resolving it:
// the C0 controls (U+0000-U+001F) and the space (U+0020).
const isURLPadding = (charCode: number): boolean => charCode <= 0x20;

/**
 * Strips the characters a browser ignores when working out what scheme a URL
 * has: leading and trailing C0 controls and spaces, and tabs and newlines from
 * anywhere inside the value.
 *
 * The allowlist is checked against the normalized URL and the normalized URL is
 * what gets emitted, so there's no gap between what was approved and what the
 * browser will navigate to. Padding like `href="  https://example.com"` is a
 * real link to a browser, and used to be discarded here for looking like one
 * with no scheme.
 */
function normalizeURL(url: string): string {
  // Tabs and newlines are ignored anywhere inside a URL, not only at the ends.
  const stripped = url.replace(/[\t\n\r]/g, '');
  let start = 0;
  let end = stripped.length;
  while (start < end && isURLPadding(stripped.charCodeAt(start))) {
    start++;
  }
  while (end > start && isURLPadding(stripped.charCodeAt(end - 1))) {
    end--;
  }
  return stripped.slice(start, end);
}

const NON_PRESERVING_TAGS = ['A', 'SPAN'];

// Tags that say that their content is bold, and that a `style` can say is not.
const BOLD_TAGS = ['B', 'STRONG'];

/**
 * Whether the `style` of the element says that its text has a weight that is not
 * bold: `font-weight: normal`, `lighter`, or a number below 600.
 *
 * Google Docs wraps all that it copies in `<b style="font-weight:normal">`. The
 * style does not stay in the output, and then the tag says that all of the text
 * is bold. A later declaration of the same property wins, as in CSS.
 */
function hasNormalWeight(node: ParsedElement): boolean {
  const style = node.attributes.find(attr => attr.name === 'style')?.value;
  if (!style) {
    return false;
  }
  let weight: string | null = null;
  for (const [, value] of style.matchAll(
    /(?:^|;)\s*font-weight\s*:([^;]*)/gi,
  )) {
    weight = value;
  }
  if (weight === null) {
    return false;
  }
  const keyword = weight
    .replace(/!\s*important/i, '')
    .trim()
    .toLowerCase();
  return (
    keyword === 'normal' ||
    keyword === 'lighter' ||
    (/^\d+$/.test(keyword) && Number(keyword) < 600)
  );
}

const SELF_CLOSING = ['IMG', 'BR', 'HR'];

// A tag that is not allowed is removed and its content stays, so that the text
// of a creator is never lost with the markup around it. The show notes in the
// Django app (`sanitize` in `pinecast/helpers.py`) work the same way. These
// tags are the exception. Their content is code, metadata, a drawing, a list of
// choices, or a fallback for a browser that cannot show the element. It is not
// text for a reader.
const DROPPED_WITH_CONTENT = [
  'APPLET',
  'AUDIO',
  'CANVAS',
  'DATALIST',
  'IFRAME',
  'NOEMBED',
  'NOFRAMES',
  'NOSCRIPT',
  'OBJECT',
  'SCRIPT',
  'SELECT',
  'STYLE',
  'SVG',
  'TEMPLATE',
  'TITLE',
  'VIDEO',
];

// Tags of elements that a browser shows as blocks. If such a tag is not
// allowed, its content takes the place of the element in a `div`. A `div` keeps
// the text of two blocks apart, and it can carry a language mark.
const BLOCK_TAGS = [
  'ADDRESS',
  'ARTICLE',
  'ASIDE',
  'BLOCKQUOTE',
  'CAPTION',
  'CENTER',
  'DD',
  'DETAILS',
  'DIALOG',
  'DIR',
  'DIV',
  'DL',
  'DT',
  'FIELDSET',
  'FIGCAPTION',
  'FIGURE',
  'FOOTER',
  'FORM',
  'H1',
  'H2',
  'H3',
  'H4',
  'H5',
  'H6',
  'HEADER',
  'HGROUP',
  'LEGEND',
  'LI',
  'MAIN',
  'MENU',
  'NAV',
  'OL',
  'P',
  'PRE',
  'SEARCH',
  'SECTION',
  'SUMMARY',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'UL',
];

export default function sanitize(
  markup: string,
  {
    allowedTags = DEFAULT_ALLOWED_TAGS,
    allowedAttributes = DEFAULT_ALLOWED_ATTRIBUTES,
    renameTags = {},
  }: {
    allowedTags?: typeof DEFAULT_ALLOWED_TAGS;
    allowedAttributes?: typeof DEFAULT_ALLOWED_ATTRIBUTES;
    /**
     * Allowed tags that are written with another name, in upper case. The server
     * does this to `h4` to `h6` in show notes (`DEMOTED_HEADINGS` in
     * `pinecast/helpers.py`). The attributes are the ones of the original tag.
     */
    renameTags?: Record<string, string>;
  } = {},
): string {
  if (!markup) {
    return '';
  }

  const output: Array<string> = [];

  /**
   * Writes what stands in the place of an element that is not allowed, and
   * returns what must follow its content. The content stays, and the caller
   * writes it.
   *
   * A block takes a `div` and any other element takes a `span`, if the caller
   * allows that tag. A `span` has to have a language mark to be worth its
   * place. The marks move to the wrapper, if the caller allows it to carry
   * them. All other attributes of the element are dropped.
   *
   * A block that cannot have a `div` is set apart with spaces. Without them the
   * text of one block would run into the text of the next.
   */
  function stand(node: ParsedElement): string {
    const isBlock = BLOCK_TAGS.includes(node.tagName);
    const wrapper = isBlock ? 'DIV' : 'SPAN';
    if (!allowedTags.includes(wrapper)) {
      if (isBlock) {
        output.push(' ');
        return ' ';
      }
      return '';
    }
    const wrapperAttributes = [
      ...(allowedAttributes[wrapper] || []),
      ...(allowedAttributes['*'] || []),
    ];
    const marks = node.attributes.filter(
      attr =>
        LANGUAGE_MARK_ATTRIBUTES.includes(attr.name) &&
        wrapperAttributes.includes(attr.name) &&
        isSafeLanguageMark(attr.name, attr.value),
    );
    if (!isBlock && !marks.length) {
      return '';
    }
    const lowerName = wrapper.toLowerCase();
    output.push(`<${lowerName}`);
    for (const {name, value} of marks) {
      output.push(` ${escapeHTML(name)}="${escapeHTML(value)}"`);
    }
    output.push('>');
    return `</${lowerName}>`;
  }

  function walk(nodes: Array<ParsedNode>) {
    for (const node of nodes) {
      if (node.type === 'text') {
        output.push(escapeHTML(node.value));
        continue;
      }

      const {tagName} = node;
      if (!allowedTags.includes(tagName)) {
        if (DROPPED_WITH_CONTENT.includes(tagName)) {
          // Markup like `<script>alert(1)</script>` does not leave its body
          // behind as text.
          continue;
        }
        const closing = stand(node);
        walk(node.children);
        output.push(closing);
        continue;
      }

      if (BOLD_TAGS.includes(tagName) && hasNormalWeight(node)) {
        // Its content is not bold, so the tag has no place in the output.
        walk(node.children);
        continue;
      }

      const elemAllowedAttributes = [
        ...(allowedAttributes[tagName] || []),
        ...(allowedAttributes['*'] || []),
      ];
      const hasAllowedAttributes = node.attributes.some(attr =>
        elemAllowedAttributes.includes(attr.name),
      );
      const shouldPreserveTag =
        !NON_PRESERVING_TAGS.includes(tagName) || hasAllowedAttributes;
      const lowerName = (renameTags[tagName] ?? tagName).toLowerCase();

      if (shouldPreserveTag) {
        output.push(`<${lowerName}`);

        if (hasAllowedAttributes) {
          for (const attr of node.attributes) {
            if (!elemAllowedAttributes.includes(attr.name)) {
              continue;
            }
            let {value} = attr;
            if (attr.name === 'src' || attr.name === 'href') {
              value = normalizeURL(value);
              if (
                !ALLOWED_SCHEMES.some(scheme =>
                  value.toLowerCase().startsWith(scheme),
                )
              ) {
                continue;
              }
            } else if (
              LANGUAGE_MARK_ATTRIBUTES.includes(attr.name) &&
              !isSafeLanguageMark(attr.name, value)
            ) {
              continue;
            }
            output.push(` ${escapeHTML(attr.name)}="${escapeHTML(value)}"`);
          }
        }

        // An image with no `alt` is read out by its file name. An empty `alt`
        // says that the image is for decoration, which is the better default
        // for an image that a creator did not describe.
        if (
          tagName === 'IMG' &&
          elemAllowedAttributes.includes('alt') &&
          !node.attributes.some(attr => attr.name === 'alt')
        ) {
          output.push(' alt=""');
        }

        output.push('>');

        if (SELF_CLOSING.includes(tagName)) {
          // Void elements never have children and take no closing tag.
          continue;
        }
      }

      walk(node.children);

      if (shouldPreserveTag) {
        output.push(`</${lowerName}>`);
      }
    }
  }

  walk(parseHTMLBody(markup));

  return output.join('');
}
